import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Plus, Pencil, Trash2, TicketPercent } from "lucide-react";
import api, { formatIDR } from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const EMPTY_FORM = {
  code: "", name: "", type: "percent", value: "",
  min_order_amount: "", max_discount_amount: "",
  valid_from: "", valid_until: "", max_uses: "", active: true,
};

export default function AdminVouchers() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback(outletId);
  const [vouchers, setVouchers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => run(async () => {
    if (!outletId) return;
    const { data } = await api.get("/vouchers", { params: { outlet_id: outletId } });
    setVouchers(data || []);
  }), [outletId, run]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      code: v.code || "", name: v.name || "", type: v.type || "percent",
      value: String(v.value ?? ""),
      min_order_amount: v.min_order_amount ? String(v.min_order_amount) : "",
      max_discount_amount: v.max_discount_amount ? String(v.max_discount_amount) : "",
      valid_from: v.valid_from || "", valid_until: v.valid_until || "",
      max_uses: v.max_uses ? String(v.max_uses) : "", active: v.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Kode voucher wajib diisi");
    if (!form.value || Number(form.value) <= 0) return toast.error("Nilai diskon harus lebih dari 0");
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      type: form.type,
      value: Number(form.value),
      min_order_amount: form.min_order_amount ? Number(form.min_order_amount) : 0,
      max_discount_amount: form.type === "percent" && form.max_discount_amount ? Number(form.max_discount_amount) : 0,
      valid_from: form.valid_from || "",
      valid_until: form.valid_until || "",
      max_uses: form.max_uses ? Number(form.max_uses) : 0,
      active: form.active,
    };
    if (payload.valid_from && payload.valid_until && payload.valid_until < payload.valid_from) {
      return toast.error("Tanggal berakhir harus setelah tanggal mulai");
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/vouchers/${editing.id}`, payload);
      else await api.post("/vouchers", { ...payload, outlet_id: outletId });
      toast.success(editing ? "Voucher diperbarui" : "Voucher dibuat");
      setOpen(false); setEditing(null); setForm(EMPTY_FORM); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan voucher");
    } finally { setSaving(false); }
  };

  const toggleActive = async (v) => {
    setTogglingId(v.id);
    try {
      await api.put(`/vouchers/${v.id}`, { active: !v.active });
      toast.success(v.active ? `Voucher ${v.code} dinonaktifkan` : `Voucher ${v.code} diaktifkan`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah status voucher");
    } finally { setTogglingId(""); }
  };

  const remove = async () => {
    const v = confirmDelete;
    setConfirmDelete(null);
    if (!v) return;
    try {
      await api.delete(`/vouchers/${v.id}`);
      toast.success("Voucher dihapus");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus voucher");
    }
  };

  const statusLabel = (v) => {
    if (v.active === false) return ["Nonaktif", "bg-accent text-muted-foreground"];
    const today = dayjs().format("YYYY-MM-DD");
    if (v.valid_until && v.valid_until < today) return ["Kedaluwarsa", "bg-destructive/10 text-destructive"];
    if (v.valid_from && v.valid_from > today) return ["Belum berlaku", "bg-warning/15 text-warning"];
    if (v.max_uses > 0 && (v.used_count || 0) >= v.max_uses) return ["Kuota habis", "bg-destructive/10 text-destructive"];
    return ["Aktif", "bg-success/10 text-success"];
  };

  const describeValue = (v) => v.type === "percent"
    ? `${v.value}%${(v.max_discount_amount || 0) > 0 ? ` (maks ${formatIDR(v.max_discount_amount)})` : ""}`
    : formatIDR(v.value);

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-vouchers-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Voucher Promosi" description={<> Kelola kode voucher untuk outlet <b>{activeOutlet?.name || "-"}</b> </>} actions={<Button onClick={openCreate} className="rounded-full" data-testid="add-voucher-btn">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Buat Voucher</span><span className="sm:hidden">Buat</span>
        </Button>} />

      <div className="surface-card overflow-x-auto">
        <div className="hidden lg:grid grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_120px] gap-3 border-b border-border bg-accent/40 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">
          <span>Kode &amp; Nama</span>
          <span>Diskon</span>
          <span>Masa Berlaku</span>
          <span>Min. Order</span>
          <span>Kuota</span>
          <span className="text-right">Aksi</span>
        </div>

        {vouchers.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            <TicketPercent className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Belum ada voucher. Buat voucher pertama untuk outlet ini.</p>
          </div>
        ) : vouchers.map((v) => {
          const [label, cls] = statusLabel(v);
          return (
            <div key={v.id} className="border-b border-border last:border-0 px-4 py-3" data-testid={`voucher-row-${v.id}`}>
              <div className="lg:grid lg:grid-cols-[1.4fr_1fr_1fr_0.8fr_0.8fr_120px] gap-3 items-center space-y-2 lg:space-y-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-sm" data-testid={`voucher-code-${v.id}`}>{v.code}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`} data-testid={`voucher-status-${v.id}`}>{label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{v.name || "-"}</p>
                </div>
                <p className="text-sm font-medium">{describeValue(v)}</p>
                <p className="text-xs text-muted-foreground">
                  {v.valid_from || "∞"} s/d {v.valid_until || "∞"}
                </p>
                <p className="text-xs text-muted-foreground">{(v.min_order_amount || 0) > 0 ? formatIDR(v.min_order_amount) : "-"}</p>
                <p className="text-xs text-muted-foreground" data-testid={`voucher-quota-${v.id}`}>
                  {v.used_count || 0}{v.max_uses > 0 ? ` / ${v.max_uses}` : " (tanpa batas)"}
                </p>
                <div className="flex items-center gap-2 lg:justify-end">
                  <Switch aria-label={`Aktifkan voucher ${v.code}`} checked={v.active !== false} disabled={togglingId === v.id}
                          onCheckedChange={() => toggleActive(v)} data-testid={`voucher-toggle-${v.id}`} />
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" aria-label={`Edit voucher ${v.code}`} onClick={() => openEdit(v)} data-testid={`voucher-edit-${v.id}-btn`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" aria-label={`Hapus voucher ${v.code}`} onClick={() => setConfirmDelete(v)} data-testid={`voucher-delete-${v.id}-btn`}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Form Create/Edit Voucher ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="admin-dialog max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Voucher ${editing.code}` : "Buat Voucher Baru"}</DialogTitle>
            <DialogDescription>
              {editing ? "Ubah aturan voucher di bawah ini." : "Voucher dapat dipakai pelanggan saat checkout atau oleh kasir."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="adminvouchers-field-1">Kode Voucher *</Label>
                <Input id="adminvouchers-field-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                       placeholder="HEMAT20" className="bg-white uppercase font-mono" maxLength={30} data-testid="voucher-form-code" />
              </div>
              <div>
                <Label htmlFor="adminvouchers-field-2">Nama Voucher</Label>
                <Input id="adminvouchers-field-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="Promo Akhir Tahun" className="bg-white" data-testid="voucher-form-name" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="adminvouchers-select-10">Tipe Diskon *</Label>
                <Select value={form.type} onValueChange={(val) => setForm({ ...form, type: val })}>
                  <SelectTrigger id="adminvouchers-select-10" className="mt-1 bg-white" data-testid="voucher-form-type">
                    <SelectValue placeholder="Pilih tipe" />
                  </SelectTrigger>
                  <SelectContent className="admin-popup">
                    <SelectItem value="percent">Persentase (%)</SelectItem>
                    <SelectItem value="fixed">Nominal (Rp)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="adminvouchers-field-3">{form.type === "percent" ? "Diskon (%) *" : "Potongan (Rp) *"}</Label>
                <Input id="adminvouchers-field-3" type="number" min="1" max={form.type === "percent" ? 100 : undefined} value={form.value}
                       onChange={(e) => setForm({ ...form, value: e.target.value })}
                       placeholder={form.type === "percent" ? "20" : "20000"} className="bg-white" data-testid="voucher-form-value" />
              </div>
            </div>
            {form.type === "percent" && (
              <div>
                <Label htmlFor="adminvouchers-field-4">Batas Maksimal Diskon (Rp, opsional)</Label>
                <Input id="adminvouchers-field-4" type="number" min="0" value={form.max_discount_amount}
                       onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
                       placeholder="Kosongkan jika tanpa batas" className="bg-white" data-testid="voucher-form-max-discount" />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="adminvouchers-field-5">Minimum Order (Rp)</Label>
                <Input id="adminvouchers-field-5" type="number" min="0" value={form.min_order_amount}
                       onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                       placeholder="0 = tanpa minimum" className="bg-white" data-testid="voucher-form-min-order" />
              </div>
              <div>
                <Label htmlFor="adminvouchers-field-6">Kuota Pemakaian</Label>
                <Input id="adminvouchers-field-6" type="number" min="0" value={form.max_uses}
                       onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                       placeholder="0 = tanpa batas" className="bg-white" data-testid="voucher-form-quota" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="adminvouchers-field-7">Berlaku Mulai</Label>
                <Input id="adminvouchers-field-7" type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                       className="bg-white" data-testid="voucher-form-valid-from" />
              </div>
              <div>
                <Label htmlFor="adminvouchers-field-8">Berlaku Sampai</Label>
                <Input id="adminvouchers-field-8" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                       className="bg-white" data-testid="voucher-form-valid-until" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="adminvouchers-field-9">Voucher aktif</Label>
              <Switch id="adminvouchers-field-9" checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} data-testid="voucher-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="voucher-form-save">
              {saving ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Buat Voucher"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="admin-dialog max-w-sm">
          <DialogHeader>
            <DialogTitle>Hapus Voucher?</DialogTitle>
            <DialogDescription>
              Voucher <b>{confirmDelete?.code}</b> akan dihapus permanen. Pesanan yang sudah memakai voucher ini tidak terpengaruh.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Batal</Button>
            <Button variant="destructive" onClick={remove} data-testid="voucher-delete-confirm">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}